"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  pagedMaxWaitMs,
  pagedQuietDebounceMs,
} from "@/app/_components/editor/constants";
import {
  pagedPreviewCompleteEvent,
  pagedPreviewFallbackEvent,
  pagedPreviewUpdateMessageType,
  type PagedPreviewUpdateMessage,
} from "@/src/lib/pdf/print-document";

export const pagedPreviewSettleTimeoutMs =
  process.env.NODE_ENV === "test" ? 250 : 8_000;

export { pagedMaxWaitMs, pagedQuietDebounceMs };

export type PagedPreviewSlot = "a" | "b";

/** Host payload: cold-load shell when shellKey changes; otherwise warm-update. */
export type PagedPreviewRequest = {
  shellKey: string;
  shellSrcDoc: string;
  revision: string;
  bodyHtml: string;
  printCss: string;
  dir: "ltr" | "rtl";
  lang: string;
  previewSurfaceBackground: string;
  mermaidConfig: Record<string, unknown>;
};

export function measurePagedPreviewIframe(iframe: HTMLIFrameElement): {
  height: number;
  pageCount: number;
} | null {
  const contentDocument = iframe.contentDocument;

  if (!contentDocument?.documentElement) {
    return null;
  }

  const pagesRoot = contentDocument.querySelector(".pagedjs_pages");
  const measuredRoot =
    (pagesRoot as HTMLElement | null) ??
    contentDocument.body ??
    contentDocument.documentElement;
  const height = Math.max(
    measuredRoot.scrollHeight,
    measuredRoot.offsetHeight,
  );

  if (height <= 0) {
    return null;
  }

  const pages = contentDocument.querySelectorAll(".pagedjs_page").length;
  const dataCount = Number(
    contentDocument.documentElement.dataset.pageCount ?? "",
  );
  const pageCount =
    Number.isFinite(dataCount) && dataCount > 0
      ? dataCount
      : pages > 0
        ? pages
        : 1;

  return { height, pageCount };
}

export function isPagedPreviewSettled(iframe: HTMLIFrameElement): boolean {
  const status = iframe.contentDocument?.documentElement?.dataset.pagedStatus;
  return status === "complete" || status === "fallback";
}

/** True when the iframe document revision matches the pending preview. */
export function iframeMatchesPreviewRevision(
  iframe: HTMLIFrameElement,
  revision: string,
) {
  if (!revision) {
    return Boolean(iframe.contentDocument?.documentElement);
  }

  return (
    iframe.contentDocument?.documentElement?.dataset.previewRevision ===
    revision
  );
}

/** @deprecated Prefer iframeMatchesPreviewRevision — kept for older tests. */
export function iframeMatchesPreviewSrcDoc(
  iframe: HTMLIFrameElement,
  srcDoc: string,
) {
  const match = /data-preview-revision="([^"]+)"/.exec(srcDoc);
  const expected = match?.[1] ?? "";

  if (!expected) {
    return Boolean(iframe.contentDocument?.documentElement);
  }

  return iframeMatchesPreviewRevision(iframe, expected);
}

export function canPromotePagedPreviewIframe(
  iframe: HTMLIFrameElement,
  revisionOrSrcDoc: string,
) {
  if (!revisionOrSrcDoc) {
    return false;
  }

  // Full srcDoc (tests / legacy) vs bare revision token.
  const revision = revisionOrSrcDoc.includes("data-preview-revision=")
    ? (/data-preview-revision="([^"]+)"/.exec(revisionOrSrcDoc)?.[1] ?? "")
    : revisionOrSrcDoc;

  return (
    Boolean(revision) &&
    iframeMatchesPreviewRevision(iframe, revision) &&
    isPagedPreviewSettled(iframe)
  );
}

function forcePagedPreviewFallback(iframe: HTMLIFrameElement) {
  const root = iframe.contentDocument?.documentElement;

  if (!root) {
    return;
  }

  const status = root.dataset.pagedStatus;

  if (status !== "complete" && status !== "fallback") {
    root.dataset.pagedStatus = "fallback";
  }
}

function slotHasWarmShell(
  iframe: HTMLIFrameElement | null,
  shellKey: string,
  slotShellKey: string,
) {
  if (!iframe || !shellKey || slotShellKey !== shellKey) {
    return false;
  }

  const win = iframe.contentWindow as
    | (Window & { __markdown2pdfRepaginate?: (payload: unknown) => void })
    | null;

  return (
    Boolean(iframe.contentDocument?.documentElement) &&
    typeof win?.__markdown2pdfRepaginate === "function"
  );
}

function postWarmUpdate(
  iframe: HTMLIFrameElement,
  request: PagedPreviewRequest,
) {
  const message: PagedPreviewUpdateMessage = {
    type: pagedPreviewUpdateMessageType,
    revision: request.revision,
    bodyHtml: request.bodyHtml,
    printCss: request.printCss,
    dir: request.dir,
    lang: request.lang,
    previewSurfaceBackground: request.previewSurfaceBackground,
    mermaidConfig: request.mermaidConfig,
  };

  // srcdoc iframes report location.origin as the literal string "null", so
  // postMessage target-origin checks are unreliable. Same-origin sandbox lets
  // us invoke the in-page API directly.
  const win = iframe.contentWindow as
    | (Window & {
        __markdown2pdfRepaginate?: (payload: PagedPreviewUpdateMessage) => void;
      })
    | null;

  if (typeof win?.__markdown2pdfRepaginate === "function") {
    win.__markdown2pdfRepaginate(message);
    return;
  }

  win?.postMessage(message, "*");
}

export function usePagedPreviewSlot(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  srcDoc: string,
  revision: string,
  onInteract: (() => void) | undefined,
  onSettled: (metrics: { height: number; pageCount: number }) => void,
  onMetrics?: (metrics: { height: number; pageCount: number }) => void,
  allowForceSettleRef?: React.RefObject<boolean>,
) {
  useLayoutEffect(() => {
    const iframe = iframeRef.current;

    if (!iframe || (!srcDoc && !revision)) {
      return;
    }

    let cancelled = false;
    let wiredDocument: Document | null = null;
    let settledNotified = false;
    let observer: ResizeObserver | null = null;
    const timeoutIds: number[] = [];
    let settleTimeoutId: number | null = null;
    let statusPollId: number | null = null;
    let metricsFrameId: number | null = null;
    const contentWindow = iframe.contentWindow;

    const clearHostTimers = () => {
      if (settleTimeoutId !== null) {
        window.clearTimeout(settleTimeoutId);
        settleTimeoutId = null;
      }

      if (statusPollId !== null) {
        window.clearInterval(statusPollId);
        statusPollId = null;
      }

      if (metricsFrameId !== null) {
        (contentWindow ?? window).cancelAnimationFrame(metricsFrameId);
        metricsFrameId = null;
      }

      timeoutIds.splice(0).forEach((id) => window.clearTimeout(id));
    };

    const publishMetrics = () => {
      if (cancelled) {
        return;
      }

      const metrics = measurePagedPreviewIframe(iframe);

      if (metrics) {
        onMetrics?.(metrics);
      }
    };

    const scheduleMetrics = () => {
      if (cancelled || metricsFrameId !== null) {
        return;
      }

      const rafTarget = contentWindow ?? window;

      metricsFrameId = rafTarget.requestAnimationFrame(() => {
        metricsFrameId = null;
        publishMetrics();
      });
    };

    const markSettled = () => {
      if (cancelled || settledNotified) {
        return;
      }

      // Never promote a previous document that is still complete while the new
      // revision is loading — that was the flicker regression.
      if (!canPromotePagedPreviewIframe(iframe, revision)) {
        return;
      }

      settledNotified = true;
      clearHostTimers();

      const metrics = measurePagedPreviewIframe(iframe);

      if (metrics) {
        onSettled(metrics);
      } else {
        onSettled({ height: 0, pageCount: 1 });
      }
    };

    const setupObserver = (contentDocument: Document) => {
      const ResizeObserverConstructor =
        (contentWindow as typeof window | null)?.ResizeObserver ??
        globalThis.ResizeObserver;

      if (!ResizeObserverConstructor || !contentDocument.body) {
        return;
      }

      observer?.disconnect();
      observer = new ResizeObserverConstructor(scheduleMetrics);
      observer.observe(contentDocument.body);
      observer.observe(contentDocument.documentElement);

      const pagesRoot = contentDocument.querySelector(".pagedjs_pages");

      if (pagesRoot) {
        observer.observe(pagesRoot);
      }
    };

    function handleIframeInteract() {
      onInteract?.();
    }

    const armSettleWatch = () => {
      if (cancelled || settledNotified) {
        return;
      }

      if (canPromotePagedPreviewIframe(iframe, revision)) {
        markSettled();
        return;
      }

      if (statusPollId === null) {
        statusPollId = window.setInterval(() => {
          if (cancelled || settledNotified) {
            return;
          }

          markSettled();
        }, 50);
      }

      if (settleTimeoutId === null) {
        settleTimeoutId = window.setTimeout(() => {
          settleTimeoutId = null;

          if (cancelled || settledNotified) {
            return;
          }

          // Only force-fallback the document that matches this revision. If the
          // iframe still shows a stale revision, keep waiting for load/update.
          if (!iframeMatchesPreviewRevision(iframe, revision)) {
            armSettleWatch();
            return;
          }

          // Buffer updates behind an already-visible preview must not flash
          // unfinished pages. Keep polling for a real complete/fallback.
          // Read from a ref so active-slot flips do not remount this effect.
          if (allowForceSettleRef && !allowForceSettleRef.current) {
            return;
          }

          forcePagedPreviewFallback(iframe);
          markSettled();
        }, pagedPreviewSettleTimeoutMs);
      }
    };

    const handleLoad = () => {
      if (cancelled) {
        return;
      }

      const contentDocument = iframe.contentDocument;

      if (!contentDocument?.documentElement) {
        return;
      }

      // Same document: sync recovery and the load event can both run; wire once.
      // New navigation: re-wire even if an earlier recover bound a stale document.
      if (wiredDocument === contentDocument) {
        armSettleWatch();
        return;
      }

      if (wiredDocument) {
        wiredDocument.removeEventListener(
          pagedPreviewCompleteEvent,
          markSettled,
        );
        wiredDocument.removeEventListener(
          pagedPreviewFallbackEvent,
          markSettled,
        );
        wiredDocument.removeEventListener("mousedown", handleIframeInteract);
      }

      wiredDocument = contentDocument;
      settledNotified = false;
      clearHostTimers();

      contentDocument.addEventListener(
        pagedPreviewCompleteEvent,
        markSettled,
      );
      contentDocument.addEventListener(
        pagedPreviewFallbackEvent,
        markSettled,
      );
      contentDocument.addEventListener("mousedown", handleIframeInteract);
      setupObserver(contentDocument);
      publishMetrics();
      armSettleWatch();

      timeoutIds.push(
        window.setTimeout(publishMetrics, 50),
        window.setTimeout(publishMetrics, 150),
        window.setTimeout(publishMetrics, 400),
        window.setTimeout(publishMetrics, 800),
        window.setTimeout(publishMetrics, 1500),
      );
    };

    iframe.addEventListener("load", handleLoad);

    // React applies srcDoc during commit, before this effect. If load already
    // fired, wire settle against the current document. Revision gating prevents
    // treating a stale complete document as the new preview.
    // Warm updates change revision without a load — re-arm settle watch.
    if (
      iframe.contentDocument?.documentElement &&
      iframe.contentDocument.readyState === "complete"
    ) {
      handleLoad();
    } else {
      // Missed-load safety / warm update: poll until matching revision settles.
      armSettleWatch();
    }

    return () => {
      cancelled = true;
      iframe.removeEventListener("load", handleLoad);
      observer?.disconnect();
      wiredDocument?.removeEventListener(
        pagedPreviewCompleteEvent,
        markSettled,
      );
      wiredDocument?.removeEventListener(
        pagedPreviewFallbackEvent,
        markSettled,
      );
      wiredDocument?.removeEventListener("mousedown", handleIframeInteract);
      clearHostTimers();
    };
  }, [
    allowForceSettleRef,
    iframeRef,
    revision,
    srcDoc,
    onInteract,
    onSettled,
    onMetrics,
  ]);
}

function slotIframe(
  slot: PagedPreviewSlot,
  iframeARef: React.RefObject<HTMLIFrameElement | null>,
  iframeBRef: React.RefObject<HTMLIFrameElement | null>,
) {
  return slot === "a" ? iframeARef.current : iframeBRef.current;
}

export function usePagedPreviewIframe(
  request: PagedPreviewRequest | null,
  onInteract?: () => void,
) {
  const iframeARef = useRef<HTMLIFrameElement>(null);
  const iframeBRef = useRef<HTMLIFrameElement>(null);
  const [slotSrc, setSlotSrc] = useState<Record<PagedPreviewSlot, string>>({
    a: "",
    b: "",
  });
  const [slotShellKey, setSlotShellKey] = useState<
    Record<PagedPreviewSlot, string>
  >({ a: "", b: "" });
  const [slotRevision, setSlotRevision] = useState<
    Record<PagedPreviewSlot, string>
  >({ a: "", b: "" });
  const [activeSlot, setActiveSlot] = useState<PagedPreviewSlot>("a");
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const [ready, setReady] = useState(false);
  // Bumped when quiet debounce or max-wait allows dispatching a free flight.
  const [dispatchNonce, setDispatchNonce] = useState(0);
  const activeSlotRef = useRef<PagedPreviewSlot>("a");
  const readyRef = useRef(false);
  const slotSrcRef = useRef(slotSrc);
  const slotRevisionRef = useRef(slotRevision);
  const slotShellKeyRef = useRef(slotShellKey);
  const latestRequestRef = useRef(request);
  const flightActiveRef = useRef(false);
  const dispatchedRevisionRef = useRef("");
  const dirtySinceRef = useRef<number | null>(null);
  const schedulerAllowedRef = useRef(false);
  const quietTimerRef = useRef<number | null>(null);
  const maxWaitTimerRef = useRef<number | null>(null);
  const contentHeightRef = useRef<number | null>(null);
  const pageCountRef = useRef(1);
  const lastSettledRevisionRef = useRef<Record<PagedPreviewSlot, string>>({
    a: "",
    b: "",
  });
  const allowForceSettleARef = useRef(true);
  const allowForceSettleBRef = useRef(true);
  const [queuedRevision, setQueuedRevision] = useState(
    request?.revision ?? "",
  );

  /* Latest-value mirrors for async/event callbacks. Must update during render
   * so handlers scheduled in the same turn see current state (not a frame late). */
  /* eslint-disable react-hooks/refs -- intentional latest-value ref mirrors */
  activeSlotRef.current = activeSlot;
  readyRef.current = ready;
  slotSrcRef.current = slotSrc;
  slotRevisionRef.current = slotRevision;
  slotShellKeyRef.current = slotShellKey;
  latestRequestRef.current = request;
  contentHeightRef.current = contentHeight;
  pageCountRef.current = pageCount;
  // Force-settle only for the first paint or the currently visible slot.
  // Inactive buffer updates must wait for a real Paged.js complete.
  allowForceSettleARef.current = !ready || activeSlot === "a";
  allowForceSettleBRef.current = !ready || activeSlot === "b";
  /* eslint-enable react-hooks/refs */

  const clearSchedulerTimers = useCallback(() => {
    if (quietTimerRef.current !== null) {
      window.clearTimeout(quietTimerRef.current);
      quietTimerRef.current = null;
    }

    if (maxWaitTimerRef.current !== null) {
      window.clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
  }, []);

  const releaseScheduler = useCallback(() => {
    if (flightActiveRef.current) {
      return;
    }

    schedulerAllowedRef.current = true;
    setDispatchNonce((value) => value + 1);
  }, []);

  const armScheduler = useCallback(() => {
    if (flightActiveRef.current || dirtySinceRef.current === null) {
      return;
    }

    // Only reset the quiet timer on re-arm (keystrokes). Keep max-wait anchored
    // to dirtySince so continuous typing still releases after pagedMaxWaitMs.
    if (quietTimerRef.current !== null) {
      window.clearTimeout(quietTimerRef.current);
      quietTimerRef.current = null;
    }

    quietTimerRef.current = window.setTimeout(() => {
      quietTimerRef.current = null;
      releaseScheduler();
    }, pagedQuietDebounceMs);

    if (maxWaitTimerRef.current === null) {
      const elapsed = Date.now() - dirtySinceRef.current;
      const maxWaitRemaining = Math.max(0, pagedMaxWaitMs - elapsed);

      maxWaitTimerRef.current = window.setTimeout(() => {
        maxWaitTimerRef.current = null;
        releaseScheduler();
      }, maxWaitRemaining);
    }
  }, [releaseScheduler]);

  const markDirtyAndArm = useCallback(() => {
    if (dirtySinceRef.current === null) {
      dirtySinceRef.current = Date.now();
    }

    if (!flightActiveRef.current) {
      armScheduler();
    }
  }, [armScheduler]);

  const clearFlightAndMaybeReschedule = useCallback(() => {
    const shouldReschedule =
      flightActiveRef.current || Boolean(dispatchedRevisionRef.current);
    flightActiveRef.current = false;
    dispatchedRevisionRef.current = "";

    // commitSlot clears dirtySince on a successful promote of the latest. If it
    // is still set, the finished flight was an intermediate snapshot — arm the
    // next free flight without reading activeSlotRef (setState not flushed yet).
    if (dirtySinceRef.current === null) {
      clearSchedulerTimers();
      schedulerAllowedRef.current = false;
      return;
    }

    if (!shouldReschedule) {
      return;
    }

    const latest = latestRequestRef.current;

    if (!latest) {
      dirtySinceRef.current = null;
      clearSchedulerTimers();
      schedulerAllowedRef.current = false;
      return;
    }

    // Reset the dirty clock after a finished flight. Otherwise max-wait elapsed
    // during the multi-second run is already >= pagedMaxWaitMs and the next
    // flight starts immediately — recreating the A→B→C chain while typing.
    dirtySinceRef.current = Date.now();
    clearSchedulerTimers();
    armScheduler();
  }, [armScheduler, clearSchedulerTimers]);

  const commitSlot = useCallback(
    (slot: PagedPreviewSlot, metrics: { height: number; pageCount: number }) => {
      const slotRev = slotRevisionRef.current[slot];

      if (!slotRev) {
        return;
      }

      const iframe = slotIframe(slot, iframeARef, iframeBRef);

      // Promote this buffer's completed pages even if the editor has moved on.
      // Otherwise typing during a multi-second run discards every finish and the
      // preview stays frozen until the user fully stops.
      if (!iframe || !canPromotePagedPreviewIframe(iframe, slotRev)) {
        return;
      }

      lastSettledRevisionRef.current[slot] = slotRev;

      if (metrics.height > 0 && metrics.height !== contentHeightRef.current) {
        setContentHeight(metrics.height);
      }

      if (metrics.pageCount > 0 && metrics.pageCount !== pageCountRef.current) {
        setPageCount(metrics.pageCount);
      }

      if (activeSlotRef.current !== slot) {
        setActiveSlot(slot);
      }

      if (!readyRef.current) {
        setReady(true);
      }

      readyRef.current = true;

      const latest = latestRequestRef.current;

      if (latest && latest.revision === slotRev) {
        dirtySinceRef.current = null;
        clearSchedulerTimers();
        schedulerAllowedRef.current = false;
        return;
      }

      // Intermediate snapshot is visible; follow up for the newer latest after
      // a fresh quiet / max-wait window (clearFlight resets the dirty clock).
      if (latest) {
        dirtySinceRef.current = Date.now();
      }
    },
    [clearSchedulerTimers],
  );

  const tryPromoteMatchingSlot = useCallback(() => {
    if (!request) {
      return false;
    }

    const { revision } = request;

    if (slotRevision[activeSlot] === revision && ready) {
      const activeIframe = slotIframe(activeSlot, iframeARef, iframeBRef);

      if (
        activeIframe &&
        canPromotePagedPreviewIframe(activeIframe, revision)
      ) {
        dirtySinceRef.current = null;
        clearSchedulerTimers();
        schedulerAllowedRef.current = false;
        flightActiveRef.current = false;
        return true;
      }
    }

    for (const slot of ["a", "b"] as const) {
      if (slotRevision[slot] !== revision) {
        continue;
      }

      const iframe = slotIframe(slot, iframeARef, iframeBRef);

      if (iframe && canPromotePagedPreviewIframe(iframe, revision)) {
        commitSlot(
          slot,
          measurePagedPreviewIframe(iframe) ?? { height: 0, pageCount: 1 },
        );
        flightActiveRef.current = false;
        return true;
      }
    }

    return false;
  }, [
    activeSlot,
    clearSchedulerTimers,
    commitSlot,
    ready,
    request,
    slotRevision,
  ]);

  const applyToSlot = useCallback(
    (slot: PagedPreviewSlot, next: PagedPreviewRequest) => {
      const iframe = slotIframe(slot, iframeARef, iframeBRef);
      const currentShell = slotShellKeyRef.current[slot];
      const canWarm = slotHasWarmShell(iframe, next.shellKey, currentShell);

      flightActiveRef.current = true;
      dispatchedRevisionRef.current = next.revision;
      schedulerAllowedRef.current = false;
      clearSchedulerTimers();

      setSlotRevision((previous) => ({ ...previous, [slot]: next.revision }));

      if (canWarm && iframe) {
        // Keep shell srcDoc; warm-repaginate in place (coalesce inside iframe).
        postWarmUpdate(iframe, next);
        return;
      }

      setSlotShellKey((previous) => ({ ...previous, [slot]: next.shellKey }));
      setSlotSrc((previous) => {
        if (previous[slot] === next.shellSrcDoc) {
          return previous;
        }

        return { ...previous, [slot]: next.shellSrcDoc };
      });
    },
    [clearSchedulerTimers],
  );

  // While ready and dirty (or becoming dirty), arm quiet + max-wait. Never
  // start a flight from this effect — layout dispatch consumes the release.
  useEffect(() => {
    if (!request || !ready) {
      if (!request) {
        dirtySinceRef.current = null;
        clearSchedulerTimers();
        schedulerAllowedRef.current = false;
        flightActiveRef.current = false;
        dispatchedRevisionRef.current = "";
        lastSettledRevisionRef.current = { a: "", b: "" };
      }

      return;
    }

    const promotedRevision = slotRevision[activeSlot];

    if (request.revision === promotedRevision && !flightActiveRef.current) {
      dirtySinceRef.current = null;
      clearSchedulerTimers();
      schedulerAllowedRef.current = false;
      return;
    }

    if (request.revision !== promotedRevision) {
      markDirtyAndArm();
    }
  }, [
    activeSlot,
    clearSchedulerTimers,
    markDirtyAndArm,
    ready,
    request,
    slotRevision,
  ]);

  // Queue a changed document after commit so the current iframe remains
  // visible while the other slot paginates the new source. Also promote a
  // buffer that already finished when the settle event was missed — but only
  // when the iframe document revision matches the pending request.
  // After the first paint, further flights wait for quiet debounce or max-wait
  // and never interrupt an in-flight run.
  useLayoutEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!request) {
      if (queuedRevision !== "") {
        setQueuedRevision("");
      }

      if (
        slotSrc.a !== "" ||
        slotSrc.b !== "" ||
        ready ||
        contentHeightRef.current !== null
      ) {
        setSlotSrc({ a: "", b: "" });
        setSlotShellKey({ a: "", b: "" });
        setSlotRevision({ a: "", b: "" });
        setContentHeight(null);
        setPageCount(1);
        setReady(false);
      }

      flightActiveRef.current = false;
      dispatchedRevisionRef.current = "";
      dirtySinceRef.current = null;
      schedulerAllowedRef.current = false;
      lastSettledRevisionRef.current = { a: "", b: "" };
      clearSchedulerTimers();
      return;
    }

    if (tryPromoteMatchingSlot()) {
      if (request.revision !== queuedRevision) {
        setQueuedRevision(request.revision);
      }

      return;
    }

    // In-flight: wait for the dispatched revision to settle. Do not chain.
    if (flightActiveRef.current && dispatchedRevisionRef.current) {
      const flightRevision = dispatchedRevisionRef.current;
      const flightSlot = (["a", "b"] as const).find(
        (slot) => slotRevision[slot] === flightRevision,
      );

      if (!flightSlot) {
        return;
      }

      if (flightRevision !== queuedRevision) {
        setQueuedRevision(flightRevision);
      }

      const pollId = window.setInterval(() => {
        const current = slotIframe(flightSlot, iframeARef, iframeBRef);

        if (!current || !iframeMatchesPreviewRevision(current, flightRevision)) {
          return;
        }

        if (!isPagedPreviewSettled(current)) {
          return;
        }

        window.clearInterval(pollId);

        commitSlot(
          flightSlot,
          measurePagedPreviewIframe(current) ?? { height: 0, pageCount: 1 },
        );

        clearFlightAndMaybeReschedule();
      }, 50);

      const stopId = window.setTimeout(() => {
        if (readyRef.current) {
          return;
        }

        window.clearInterval(pollId);

        const current = slotIframe(flightSlot, iframeARef, iframeBRef);
        const latest = latestRequestRef.current;

        if (
          latest &&
          current &&
          slotRevisionRef.current[flightSlot] === latest.revision &&
          iframeMatchesPreviewRevision(current, latest.revision)
        ) {
          forcePagedPreviewFallback(current);
          commitSlot(
            flightSlot,
            measurePagedPreviewIframe(current) ?? { height: 0, pageCount: 1 },
          );
        }

        clearFlightAndMaybeReschedule();
      }, pagedPreviewSettleTimeoutMs);

      return () => {
        window.clearInterval(pollId);
        window.clearTimeout(stopId);
      };
    }

    // Dispatched latest already on a slot — poll promote (missed settle).
    const pendingSlot = (["a", "b"] as const).find(
      (slot) => slotRevision[slot] === request.revision,
    );

    if (pendingSlot) {
      if (request.revision !== queuedRevision) {
        setQueuedRevision(request.revision);
      }

      const pollId = window.setInterval(() => {
        const latest = latestRequestRef.current;

        if (
          !latest ||
          slotRevisionRef.current[pendingSlot] !== latest.revision
        ) {
          return;
        }

        const current = slotIframe(pendingSlot, iframeARef, iframeBRef);

        if (
          current &&
          canPromotePagedPreviewIframe(current, latest.revision)
        ) {
          commitSlot(
            pendingSlot,
            measurePagedPreviewIframe(current) ?? { height: 0, pageCount: 1 },
          );
          flightActiveRef.current = false;
          window.clearInterval(pollId);
        }
      }, 50);

      // First paint only: force-fallback if nothing settles. Once a preview is
      // already visible, keep polling — never clear the interval on timeout
      // (warm updates can outlast the settle window; clearing left the spinner
      // stuck forever).
      const stopId = window.setTimeout(() => {
        if (readyRef.current) {
          return;
        }

        window.clearInterval(pollId);

        const latest = latestRequestRef.current;
        const current = slotIframe(pendingSlot, iframeARef, iframeBRef);

        if (
          latest &&
          current &&
          slotRevisionRef.current[pendingSlot] === latest.revision &&
          iframeMatchesPreviewRevision(current, latest.revision)
        ) {
          forcePagedPreviewFallback(current);
          commitSlot(
            pendingSlot,
            measurePagedPreviewIframe(current) ?? { height: 0, pageCount: 1 },
          );
          flightActiveRef.current = false;
        }
      }, pagedPreviewSettleTimeoutMs);

      return () => {
        window.clearInterval(pollId);
        window.clearTimeout(stopId);
      };
    }

    const inactive: PagedPreviewSlot = activeSlot === "a" ? "b" : "a";
    const target = ready ? inactive : activeSlot;

    if (slotRevision[target] === request.revision) {
      return;
    }

    // First paint: dispatch immediately. Later flights need scheduler release.
    if (ready && !schedulerAllowedRef.current) {
      if (request.revision !== queuedRevision) {
        setQueuedRevision(request.revision);
      }

      return;
    }

    if (request.revision !== queuedRevision) {
      setQueuedRevision(request.revision);
    }

    applyToSlot(target, request);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [
    activeSlot,
    applyToSlot,
    clearFlightAndMaybeReschedule,
    clearSchedulerTimers,
    commitSlot,
    dispatchNonce,
    queuedRevision,
    ready,
    request,
    slotRevision,
    slotSrc.a,
    slotSrc.b,
    tryPromoteMatchingSlot,
  ]);

  const handleActiveMetrics = useCallback(
    (slot: PagedPreviewSlot, metrics: { height: number; pageCount: number }) => {
      if (activeSlotRef.current !== slot || !readyRef.current) {
        return;
      }

      if (metrics.height > 0 && metrics.height !== contentHeightRef.current) {
        setContentHeight(metrics.height);
      }

      if (metrics.pageCount > 0 && metrics.pageCount !== pageCountRef.current) {
        setPageCount(metrics.pageCount);
      }
    },
    [],
  );

  const handleSlotSettled = useCallback(
    (slot: PagedPreviewSlot, metrics: { height: number; pageCount: number }) => {
      const slotRev = slotRevisionRef.current[slot];

      if (!slotRev) {
        return;
      }

      // Effect remount on an already-promoted active buffer must not re-enter.
      if (
        lastSettledRevisionRef.current[slot] === slotRev &&
        activeSlotRef.current === slot
      ) {
        return;
      }

      // Stale complete inactive buffer must not yank focus when its settle
      // watch remounts — only the in-flight dispatched revision may promote.
      if (
        activeSlotRef.current !== slot &&
        slotRev !== dispatchedRevisionRef.current
      ) {
        return;
      }

      // Clear even if a poll already flipped flightActive — otherwise follow-up
      // flights never start after the first intermediate promote.
      const isDispatchedRevision = slotRev === dispatchedRevisionRef.current;

      commitSlot(slot, metrics);

      if (isDispatchedRevision) {
        clearFlightAndMaybeReschedule();
      }
    },
    [clearFlightAndMaybeReschedule, commitSlot],
  );

  const handleSlotASettled = useCallback(
    (metrics: { height: number; pageCount: number }) => {
      handleSlotSettled("a", metrics);
    },
    [handleSlotSettled],
  );
  const handleSlotBSettled = useCallback(
    (metrics: { height: number; pageCount: number }) => {
      handleSlotSettled("b", metrics);
    },
    [handleSlotSettled],
  );
  const handleSlotAMetrics = useCallback(
    (metrics: { height: number; pageCount: number }) => {
      handleActiveMetrics("a", metrics);
    },
    [handleActiveMetrics],
  );
  const handleSlotBMetrics = useCallback(
    (metrics: { height: number; pageCount: number }) => {
      handleActiveMetrics("b", metrics);
    },
    [handleActiveMetrics],
  );

  usePagedPreviewSlot(
    iframeARef,
    slotSrc.a,
    slotRevision.a,
    onInteract,
    handleSlotASettled,
    handleSlotAMetrics,
    allowForceSettleARef,
  );
  usePagedPreviewSlot(
    iframeBRef,
    slotSrc.b,
    slotRevision.b,
    onInteract,
    handleSlotBSettled,
    handleSlotBMetrics,
    allowForceSettleBRef,
  );

  // True while the latest request differs from the promoted pages — including
  // dirty revisions held by the scheduler before the next flight dispatches.
  const isUpdating =
    ready &&
    Boolean(request) &&
    slotRevision[activeSlot] !== (request?.revision ?? "");

  return {
    iframeARef,
    iframeBRef,
    slotSrc,
    slotRevision,
    slotShellKey,
    activeSlot,
    contentHeight,
    pageCount,
    ready,
    isUpdating,
  };
}
